import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Navbar from "../components/Navbar";

const FAQ = () => {
  const [activeCategory, setActiveCategory] = useState("general");
  const [openQuestions, setOpenQuestions] = useState(new Set());

  const toggleQuestion = (id) => {
    const newOpenQuestions = new Set(openQuestions);
    if (newOpenQuestions.has(id)) {
      newOpenQuestions.delete(id);
    } else {
      newOpenQuestions.add(id);
    }
    setOpenQuestions(newOpenQuestions);
  };

  const categories = {
    general: "Général",
    consultation: "Consultation",
    technique: "Technique",
    paiement: "Paiement",
  };

  const faqs = {
    general: [
      {
        id: "g1",
        question: "Qu'est-ce que la Clinique Numérique Africaine ?",
        reponse:
          "La Clinique Numérique Africaine est une plateforme de télémédecine innovante qui connecte les patients africains avec des professionnels de santé qualifiés via des consultations en ligne.",
      },
      {
        id: "g2",
        question: "Dans quels pays êtes-vous présents ?",
        reponse:
          "Nous sommes actuellement présents dans plus de 30 pays africains, avec des bureaux principaux à Dakar, Abidjan et Nairobi.",
      },
      {
        id: "g3",
        question: "Comment garantissez-vous la qualité des soins ?",
        reponse:
          "Nous collaborons uniquement avec des professionnels de santé certifiés et suivons des protocoles stricts de qualité. Chaque médecin est vérifié et évalué régulièrement.",
      },
    ],
    consultation: [
      {
        id: "c1",
        question: "Comment se déroule une consultation en ligne ?",
        reponse:
          "La consultation se fait par vidéoconférence sécurisée. Vous discutez avec le médecin, qui peut prescrire des médicaments ou recommander des examens complémentaires si nécessaire.",
      },
      {
        id: "c2",
        question: "Quels types de consultations proposez-vous ?",
        reponse:
          "Nous proposons des consultations de médecine générale, de spécialistes, et des suivis psychologiques. Certaines spécialités peuvent varier selon les régions.",
      },
      {
        id: "c3",
        question: "Que faire en cas d'urgence ?",
        reponse:
          "Notre service n'est pas adapté aux urgences médicales. En cas d'urgence, contactez immédiatement les services d'urgence locaux.",
      },
    ],
    technique: [
      {
        id: "t1",
        question: "Quels équipements sont nécessaires ?",
        reponse:
          "Un smartphone, une tablette ou un ordinateur avec une connexion internet stable suffit. Notre application est optimisée pour fonctionner même avec une connexion limitée.",
      },
      {
        id: "t2",
        question: "Comment sont protégées mes données médicales ?",
        reponse:
          "Nous utilisons un cryptage de bout en bout et respectons les normes internationales de protection des données de santé. Vos informations sont stockées de manière sécurisée.",
      },
    ],
    paiement: [
      {
        id: "p1",
        question: "Quels sont les moyens de paiement acceptés ?",
        reponse:
          "Nous acceptons les paiements par mobile money, carte bancaire et transferts bancaires. Les options varient selon votre pays.",
      },
      {
        id: "p2",
        question: "Les consultations sont-elles remboursées ?",
        reponse:
          "Le remboursement dépend de votre assurance santé. Nous fournissons une facture détaillée pour faciliter vos démarches de remboursement.",
      },
    ],
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Questions Fréquentes
          </h1>
          <p className="text-xl text-gray-600">
            Trouvez rapidement des réponses à vos questions
          </p>
        </motion.div>

        {/* Catégories */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {Object.entries(categories).map(([key, label]) => (
            <motion.button
              key={key}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setActiveCategory(key)}
              className={`px-6 py-2 rounded-full font-medium transition-colors ${
                activeCategory === key
                  ? "bg-blue-600 text-white"
                  : "bg-white text-gray-600 hover:bg-gray-100"
              }`}
            >
              {label}
            </motion.button>
          ))}
        </div>

        {/* Questions et Réponses */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="space-y-6"
        >
          {faqs[activeCategory].map((faq) => (
            <motion.div
              key={faq.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-lg shadow-lg overflow-hidden"
            >
              <button
                onClick={() => toggleQuestion(faq.id)}
                className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50"
              >
                <span className="text-lg font-medium text-gray-900">
                  {faq.question}
                </span>
                <motion.span
                  animate={{ rotate: openQuestions.has(faq.id) ? 180 : 0 }}
                  className="text-blue-600"
                >
                  ▼
                </motion.span>
              </button>

              <AnimatePresence>
                {openQuestions.has(faq.id) && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="px-6 py-4 bg-gray-50"
                  >
                    <p className="text-gray-600">{faq.reponse}</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </motion.div>

        {/* Section de support */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-12 bg-blue-50 rounded-lg p-8 text-center"
        >
          <h2 className="text-2xl font-semibold text-blue-900 mb-4">
            Vous n'avez pas trouvé votre réponse ?
          </h2>
          <p className="text-blue-700 mb-6">
            Notre équipe de support est là pour vous aider
          </p>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => window.location.href = "/contact"}
            className="bg-blue-600 text-white px-8 py-3 rounded-full font-medium hover:bg-blue-700 transition-colors"
          >
            Contactez-nous
          </motion.button>
        </motion.div>
      </div>
    </div>
  );
};

export default FAQ;